import type { CapturedPhoto } from '../adapters/camera/index.js';
import { operatorExternalActivityActive } from '../auth/operatorExternalActivity.js';
import { flushLocalDb, openLocalDb } from '../db/local.js';
import { getSupabase } from '../supabase.js';

const BUCKET = 'production-lot-evidence';
const MAX_PHOTOS = 3;
const MAX_BATCH_CAPTURES = 20;
const MAX_PHOTO_BYTES = 5_000_000;
const RETRY_BASE_MS = 5_000;
const RETRY_MAX_MS = 5 * 60_000;
const ORPHANED_STAGE_TTL_MS = 24 * 60 * 60_000;
const CAPTURE_LEASE_PREFIX = 'trackeat.production-lot-capture.';
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export interface StagedProductionLotPhoto {
  id: string;
  path: string;
  mime: string;
  sizeBytes: number;
}

export interface StagedProductionLotBatchCapture {
  capture_id: string;
  captured_at: string;
  photos: Array<{
    photo_id: string;
    path: string;
    mime: string;
    sha256: string;
    size_bytes: number;
  }>;
}

export interface StagedProductionLotPhotoBatch {
  schema: 'trackeat.production-lot-photo-batch';
  version: 2;
  batch_id: string;
  captured_at: string;
  captures: StagedProductionLotBatchCapture[];
}

export interface ProductionLotBatchPhotoInput {
  photo: CapturedPhoto;
  capturedAt: string;
}

interface StageBatchInput {
  tenantId: string;
  businessId: string;
  productionId: string;
  photos: ProductionLotBatchPhotoInput[];
}

interface StageInput {
  tenantId: string;
  businessId: string;
  productionId: string;
  inputId: string;
  captureId: string;
  photos: CapturedPhoto[];
}

interface PendingPhotoRow {
  id: string;
  production_id: string;
  target_path: string;
  mime: string;
  base64: string;
  sha256: string | null;
  size_bytes: number;
  attempts: number;
}

interface DurableProductionEvidence {
  photoPaths: Set<string>;
}

interface ProductionLifecycle {
  masterExists: boolean;
  masterEvidence: DurableProductionEvidence | null;
  hasPendingCompound: boolean;
  pendingScopeUncertain: boolean;
  pendingCommandId: string | null;
  pendingEvidence: DurableProductionEvidence | null;
}

class PhotoReviewRequiredError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PhotoReviewRequiredError';
  }
}

function extensionForMime(mime: string): string {
  if (mime === 'image/png') return 'png';
  if (mime === 'image/webp') return 'webp';
  return 'jpg';
}

function decodedSize(base64: string): number {
  const hasPadding = base64.includes('=');
  if (
    base64.length === 0
    || base64.length % 4 === 1
    || (hasPadding && base64.length % 4 !== 0)
    || !/^[A-Za-z0-9+/]*={0,2}$/.test(base64)
  ) {
    return 0;
  }
  const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;
  return Math.max(0, Math.floor((base64.length * 3) / 4) - padding);
}

function bytesFromBase64(base64: string): Uint8Array {
  return Uint8Array.from(atob(base64), (char) => char.charCodeAt(0));
}

async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const input = new Uint8Array(bytes.byteLength);
  input.set(bytes);
  const digest = await crypto.subtle.digest('SHA-256', input.buffer);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

function setCaptureLease(captureId: string): void {
  try { sessionStorage.setItem(`${CAPTURE_LEASE_PREFIX}${captureId}`, '1'); } catch { /* noop */ }
}

function clearCaptureLease(captureId: string): void {
  try { sessionStorage.removeItem(`${CAPTURE_LEASE_PREFIX}${captureId}`); } catch { /* noop */ }
}

function hasCaptureLease(captureId: string): boolean {
  try { return sessionStorage.getItem(`${CAPTURE_LEASE_PREFIX}${captureId}`) === '1'; } catch { return false; }
}

function assertUuid(value: string, label: string): void {
  if (!UUID_RE.test(value)) throw new Error(`${label} non valido`);
}

function assertStageInput(input: StageInput): void {
  assertUuid(input.tenantId, 'Tenant');
  assertUuid(input.businessId, 'Locale');
  assertUuid(input.productionId, 'Produzione');
  assertUuid(input.inputId, 'Ingrediente');
  assertUuid(input.captureId, 'Acquisizione');
  if (!Array.isArray(input.photos) || input.photos.length < 1 || input.photos.length > MAX_PHOTOS) {
    throw new Error('Sono consentite da 1 a 3 foto per lotto');
  }
}

function isProductionEvidencePath(
  path: string,
  tenantId: string,
  businessId: string,
  productionId: string,
): boolean {
  const parts = path.split('/');
  if (parts.length !== 5) return false;
  const [pathTenantId, pathBusinessId, pathProductionId, captureId, fileName] = parts;
  if (
    !UUID_RE.test(pathTenantId ?? '')
    || !UUID_RE.test(pathBusinessId ?? '')
    || !UUID_RE.test(pathProductionId ?? '')
    || pathTenantId !== tenantId
    || pathBusinessId !== businessId
    || pathProductionId !== productionId
    || !UUID_RE.test(captureId ?? '')
  ) return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\.(?:jpe?g|png|webp)$/i.test(fileName ?? '');
}

function boundedError(error: unknown): string {
  const message = error instanceof Error
    ? error.message
    : error && typeof error === 'object' && typeof (error as { message?: unknown }).message === 'string'
      ? String((error as { message: string }).message)
      : String(error);
  return message.slice(0, 500);
}

function changedRows(result: unknown): number {
  return Number(
    (result as { changes?: { changes?: number } } | null)?.changes?.changes ?? 0,
  );
}

/**
 * Logout/account switch is a hard privacy boundary, especially on PWA where
 * IndexedDB is not SQLCipher-encrypted. Unsent image bytes must not survive
 * into the next authenticated session.
 */
export async function purgeProductionLotPhotoOutbox(): Promise<number> {
  const db = await openLocalDb(null);
  const captures = await db.query(
    'SELECT DISTINCT capture_id FROM pending_production_lot_photos_v1',
  );
  const result = await db.run('DELETE FROM pending_production_lot_photos_v1');
  await flushLocalDb();
  for (const value of captures.values ?? []) {
    clearCaptureLease(String((value as { capture_id?: unknown }).capture_id ?? ''));
  }
  return changedRows(result);
}

/** Purge rows for tenants no longer present in the live membership claims. */
export async function purgeProductionLotPhotosOutsideTenants(
  validTenantIds: readonly string[],
): Promise<number> {
  validTenantIds.forEach((tenantId) => assertUuid(tenantId, 'Tenant'));
  const db = await openLocalDb(null);
  const where = validTenantIds.length > 0
    ? `tenant_id NOT IN (${validTenantIds.map(() => '?').join(',')})`
    : '1 = 1';
  const captures = await db.query(
    `SELECT DISTINCT capture_id FROM pending_production_lot_photos_v1 WHERE ${where}`,
    [...validTenantIds],
  );
  const result = await db.run(
    `DELETE FROM pending_production_lot_photos_v1 WHERE ${where}`,
    [...validTenantIds],
  );
  await flushLocalDb();
  for (const value of captures.values ?? []) {
    clearCaptureLease(String((value as { capture_id?: unknown }).capture_id ?? ''));
  }
  return changedRows(result);
}

export async function productionLotPhotoReviewCount(tenantId: string): Promise<number> {
  assertUuid(tenantId, 'Tenant');
  const db = await openLocalDb(null);
  const result = await db.query(
    `SELECT COUNT(*) AS count FROM pending_production_lot_photos_v1
      WHERE tenant_id = ? AND review_required = 1`,
    [tenantId],
  );
  return Number((result.values?.[0] as { count?: unknown } | undefined)?.count ?? 0);
}

/** Manual retry never overwrites remote bytes; the digest/409 gates still apply. */
export async function retryProductionLotPhotoReviews(tenantId: string): Promise<void> {
  assertUuid(tenantId, 'Tenant');
  const now = new Date().toISOString();
  const db = await openLocalDb(null);
  await db.run(
    `UPDATE pending_production_lot_photos_v1
        SET review_required = 0, status = 'pending', next_attempt_at = ?,
            last_error = NULL, updated_at = ?
      WHERE tenant_id = ? AND review_required = 1 AND remote_accepted = 1`,
    [now, now, tenantId],
  );
  await flushLocalDb();
}

/**
 * Persiste tutte le foto prima di avviare OCR/upload. Se una riga fallisce,
 * l'intera transazione viene annullata e il wizard non registra una prova a
 * metà. I path sono definitivi e possono entrare subito nella produzione.
 */
export async function stageProductionLotPhotos(
  input: StageInput,
): Promise<StagedProductionLotPhoto[]> {
  assertStageInput(input);
  const now = new Date().toISOString();
  const staged = await Promise.all(input.photos.map(async (photo) => {
    if (!photo || typeof photo.base64 !== 'string' || typeof photo.mime !== 'string') {
      throw new Error('Foto non valida');
    }
    const mime = photo.mime.toLowerCase();
    if (!/^image\/(?:jpeg|jpg|png|webp)$/.test(mime)) {
      throw new Error('Formato foto non supportato');
    }
    const sizeBytes = decodedSize(photo.base64);
    if (sizeBytes < 1) throw new Error('Foto vuota o non valida');
    if (sizeBytes > MAX_PHOTO_BYTES) {
      throw new Error('Una foto supera il limite di 5 MB');
    }
    const bytes = bytesFromBase64(photo.base64);
    if (bytes.byteLength !== sizeBytes) throw new Error('Foto non valida');
    const id = crypto.randomUUID();
    const extension = extensionForMime(mime);
    return {
      id,
      path: `${input.tenantId}/${input.businessId}/${input.productionId}/${input.captureId}/${id}.${extension}`,
      mime,
      base64: photo.base64,
      sha256: await sha256Hex(bytes),
      sizeBytes,
    };
  }));
  const db = await openLocalDb(null);
  await db.executeTransaction(staged.map((photo) => ({
    statement: `INSERT INTO pending_production_lot_photos_v1(
      id,tenant_id,business_id,production_id,input_id,capture_id,command_id,
      target_path,mime,base64,sha256,size_bytes,status,remote_accepted,
      review_required,attempts,last_error,next_attempt_at,queued_at,updated_at
    ) VALUES(?,?,?,?,?,?,NULL,?,?,?,?,?,'staged',0,0,0,NULL,?,?,?)`,
    values: [
      photo.id,
      input.tenantId,
      input.businessId,
      input.productionId,
      input.inputId,
      input.captureId,
      photo.path,
      photo.mime,
      photo.base64,
      photo.sha256,
      photo.sizeBytes,
      now,
      now,
      now,
    ],
  })), true);
  await flushLocalDb();
  setCaptureLease(input.captureId);
  return staged.map(({ base64: _base64, sha256: _sha256, ...photo }) => photo);
}

/**
 * Persiste in una sola transazione locale una sessione rapida di fotografie.
 * Ogni scatto e una capture autonoma: il worker potra analizzare prodotti
 * diversi senza fingere che le immagini siano facce della stessa confezione.
 */
export async function stageProductionLotPhotoBatch(
  input: StageBatchInput,
): Promise<StagedProductionLotPhotoBatch> {
  assertUuid(input.tenantId, 'Tenant');
  assertUuid(input.businessId, 'Locale');
  assertUuid(input.productionId, 'Produzione');
  if (
    !Array.isArray(input.photos)
    || input.photos.length < 1
    || input.photos.length > MAX_BATCH_CAPTURES
  ) {
    throw new Error(`Sono consentite da 1 a ${MAX_BATCH_CAPTURES} foto per produzione`);
  }

  const batchId = crypto.randomUUID();
  const stagedAt = new Date().toISOString();
  const rows = await Promise.all(input.photos.map(async (entry, ordinal) => {
    const { photo, capturedAt } = entry;
    if (!photo || typeof photo.base64 !== 'string' || typeof photo.mime !== 'string') {
      throw new Error('Foto non valida');
    }
    if (
      typeof capturedAt !== 'string'
      || capturedAt.length === 0
      || Number.isNaN(Date.parse(capturedAt))
    ) {
      throw new Error('Orario di acquisizione non valido');
    }
    const mime = photo.mime.toLowerCase();
    if (!/^image\/(?:jpeg|jpg|png|webp)$/.test(mime)) {
      throw new Error('Formato foto non supportato');
    }
    const sizeBytes = decodedSize(photo.base64);
    if (sizeBytes < 1) throw new Error('Foto vuota o non valida');
    if (sizeBytes > MAX_PHOTO_BYTES) {
      throw new Error('Una foto supera il limite di 5 MB');
    }
    const bytes = bytesFromBase64(photo.base64);
    if (bytes.byteLength !== sizeBytes) throw new Error('Foto non valida');
    const captureId = crypto.randomUUID();
    const photoId = crypto.randomUUID();
    const extension = extensionForMime(mime);
    return {
      ordinal,
      capturedAt,
      captureId,
      photoId,
      path: `${input.tenantId}/${input.businessId}/${input.productionId}/${captureId}/${photoId}.${extension}`,
      mime,
      base64: photo.base64,
      sha256: await sha256Hex(bytes),
      sizeBytes,
    };
  }));

  const db = await openLocalDb(null);
  await db.executeTransaction(rows.map((row) => ({
    statement: `INSERT INTO pending_production_lot_photos_v1(
      id,tenant_id,business_id,production_id,input_id,capture_id,command_id,
      target_path,mime,base64,sha256,size_bytes,status,remote_accepted,
      review_required,attempts,last_error,next_attempt_at,queued_at,updated_at
    ) VALUES(?,?,?,?,?,?,NULL,?,?,?,?,?,'staged',0,0,0,NULL,?,?,?)`,
    values: [
      row.photoId,
      input.tenantId,
      input.businessId,
      input.productionId,
      // Compatibilita con la tabella locale V1: nel batch libero non esiste
      // ancora un ingrediente, quindi l'identita stabile e la capture stessa.
      row.captureId,
      row.captureId,
      row.path,
      row.mime,
      row.base64,
      row.sha256,
      row.sizeBytes,
      stagedAt,
      stagedAt,
      stagedAt,
    ],
  })), true);
  await flushLocalDb();
  rows.forEach((row) => setCaptureLease(row.captureId));

  // Il timestamp del batch e quello del primo scatto nell'ordine confermato
  // dall'operatore; ogni capture conserva comunque il proprio istante.
  const batchCapturedAt = rows[0]!.capturedAt;
  return {
    schema: 'trackeat.production-lot-photo-batch',
    version: 2,
    batch_id: batchId,
    captured_at: batchCapturedAt,
    captures: rows.map((row) => ({
      capture_id: row.captureId,
      captured_at: row.capturedAt,
      photos: [{
        photo_id: row.photoId,
        path: row.path,
        mime: row.mime,
        sha256: row.sha256,
        size_bytes: row.sizeBytes,
      }],
    })),
  };
}

/** Rimuove soltanto una bozza mai registrata come produzione. */
export async function discardStagedProductionLotPhotos(
  productionId: string,
): Promise<void> {
  const db = await openLocalDb(null);
  const captures = await db.query(
    `SELECT DISTINCT capture_id FROM pending_production_lot_photos_v1
      WHERE production_id = ? AND status = 'staged'`,
    [productionId],
  );
  await db.run(
    `DELETE FROM pending_production_lot_photos_v1
      WHERE production_id = ? AND status = 'staged'`,
    [productionId],
  );
  await flushLocalDb();
  for (const value of captures.values ?? []) {
    clearCaptureLease(String((value as { capture_id?: unknown }).capture_id ?? ''));
  }
}

export async function discardStagedProductionLotCapture(
  captureId: string,
): Promise<void> {
  const db = await openLocalDb(null);
  await db.run(
    `DELETE FROM pending_production_lot_photos_v1
      WHERE capture_id = ? AND status = 'staged'`,
    [captureId],
  );
  await flushLocalDb();
  clearCaptureLease(captureId);
}

function productionEvidenceFromValue(
  value: unknown,
  tenantId: string,
  productionId: string,
): DurableProductionEvidence | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const production = value as Record<string, unknown>;
  if (
    production.id !== productionId
    || production.tenant_id !== tenantId
    || typeof production.business_id !== 'string'
    || !UUID_RE.test(production.business_id)
    || !Array.isArray(production.inputs)
  ) return null;

  const photoPaths = new Set<string>();
  for (const rawInput of production.inputs) {
    if (!rawInput || typeof rawInput !== 'object' || Array.isArray(rawInput)) return null;
    const evidence = (rawInput as Record<string, unknown>).lot_evidence_v1;
    if (evidence == null) continue;
    if (!evidence || typeof evidence !== 'object' || Array.isArray(evidence)) return null;
    const record = evidence as Record<string, unknown>;
    if (
      typeof record.capture_id !== 'string'
      || !UUID_RE.test(record.capture_id)
      || !Array.isArray(record.photo_paths)
      || record.photo_paths.length < 1
      || record.photo_paths.length > MAX_PHOTOS
    ) return null;
    for (const path of record.photo_paths) {
      if (
        typeof path !== 'string'
        || !isProductionEvidencePath(
          path,
          tenantId,
          production.business_id,
          productionId,
        )
        || path.split('/')[3] !== record.capture_id
      ) return null;
      photoPaths.add(path);
    }
  }

  const rawBatch = production.lot_photo_batch_v2;
  if (rawBatch != null) {
    if (!rawBatch || typeof rawBatch !== 'object' || Array.isArray(rawBatch)) return null;
    const batch = rawBatch as Record<string, unknown>;
    if (
      batch.schema !== 'trackeat.production-lot-photo-batch'
      || batch.version !== 2
      || typeof batch.batch_id !== 'string'
      || !UUID_RE.test(batch.batch_id)
      || typeof batch.captured_at !== 'string'
      || !Array.isArray(batch.captures)
      || batch.captures.length < 1
      || batch.captures.length > MAX_BATCH_CAPTURES
    ) return null;
    const captureIds = new Set<string>();
    for (const rawCapture of batch.captures) {
      if (!rawCapture || typeof rawCapture !== 'object' || Array.isArray(rawCapture)) return null;
      const capture = rawCapture as Record<string, unknown>;
      if (
        typeof capture.capture_id !== 'string'
        || !UUID_RE.test(capture.capture_id)
        || captureIds.has(capture.capture_id)
        || typeof capture.captured_at !== 'string'
        || !Array.isArray(capture.photos)
        || capture.photos.length < 1
        || capture.photos.length > MAX_PHOTOS
      ) return null;
      captureIds.add(capture.capture_id);
      for (const rawPhoto of capture.photos) {
        if (!rawPhoto || typeof rawPhoto !== 'object' || Array.isArray(rawPhoto)) return null;
        const photo = rawPhoto as Record<string, unknown>;
        if (
          typeof photo.photo_id !== 'string'
          || !UUID_RE.test(photo.photo_id)
          || typeof photo.path !== 'string'
          || typeof photo.mime !== 'string'
          || !/^image\/(?:jpeg|jpg|png|webp)$/.test(photo.mime)
          || typeof photo.sha256 !== 'string'
          || !/^[a-f0-9]{64}$/i.test(photo.sha256)
          || typeof photo.size_bytes !== 'number'
          || !Number.isInteger(photo.size_bytes)
          || photo.size_bytes < 1
          || photo.size_bytes > MAX_PHOTO_BYTES
          || !isProductionEvidencePath(
            photo.path,
            tenantId,
            production.business_id,
            productionId,
          )
          || photo.path.split('/')[3] !== capture.capture_id
          || photo.path.split('/').at(-1)?.split('.')[0] !== photo.photo_id
          || photoPaths.has(photo.path)
        ) return null;
        photoPaths.add(photo.path);
      }
    }
  }
  return { photoPaths };
}

async function inspectProductionLifecycle(
  db: Awaited<ReturnType<typeof openLocalDb>>,
  tenantId: string,
  productionId: string,
): Promise<ProductionLifecycle> {
  const localMaster = await db.query(
    `SELECT row FROM masters_local
      WHERE table_name = 'productions'
        AND id = ? AND tenant_id = ?
      LIMIT 1`,
    [productionId, tenantId],
  );
  const masterRow = localMaster.values?.[0] as { row?: unknown } | undefined;
  let masterEvidence: DurableProductionEvidence | null = null;
  if (typeof masterRow?.row === 'string') {
    try {
      masterEvidence = productionEvidenceFromValue(
        JSON.parse(masterRow.row),
        tenantId,
        productionId,
      );
    } catch {
      // Una cache locale corrotta non autorizza mai l'upload di una bozza.
    }
  }

  const commands = await db.query(
    `SELECT command_id,master_effects_json FROM pending_stock_commands
      WHERE tenant_id = ?
      ORDER BY queued_at,command_id`,
    [tenantId],
  );
  let hasPendingCompound = false;
  let pendingScopeUncertain = false;
  let pendingCommandId: string | null = null;
  let pendingEvidence: DurableProductionEvidence | null = null;
  for (const row of commands.values ?? []) {
    try {
      const effects = JSON.parse(String(
        (row as { master_effects_json?: unknown }).master_effects_json ?? '[]',
      )) as unknown;
      if (!Array.isArray(effects)) {
        pendingScopeUncertain = true;
        continue;
      }
      for (const rawEffect of effects) {
        if (!rawEffect || typeof rawEffect !== 'object' || Array.isArray(rawEffect)) {
          pendingScopeUncertain = true;
          continue;
        }
        const effect = rawEffect as Record<string, unknown>;
        if (effect.table !== 'productions' || effect.id !== productionId) continue;
        hasPendingCompound = true;
        const commandId = String((row as { command_id?: unknown }).command_id ?? '');
        if (UUID_RE.test(commandId)) pendingCommandId = commandId;
        const patch = effect.patch && typeof effect.patch === 'object' && !Array.isArray(effect.patch)
          ? { ...(effect.patch as Record<string, unknown>), id: productionId }
          : null;
        const evidence = productionEvidenceFromValue(patch, tenantId, productionId);
        if (evidence) pendingEvidence = evidence;
      }
    } catch {
      // Un comando illeggibile resta alla riconciliazione stock; le foto non partono.
      pendingScopeUncertain = true;
    }
  }
  return {
    masterExists: masterRow != null,
    masterEvidence,
    hasPendingCompound,
    pendingScopeUncertain,
    pendingCommandId,
    pendingEvidence,
  };
}

async function reconcileProductionLifecycle(
  db: Awaited<ReturnType<typeof openLocalDb>>,
  tenantId: string,
  productionId: string,
): Promise<void> {
  const lifecycle = await inspectProductionLifecycle(db, tenantId, productionId);
  const result = await db.query(
    `SELECT id,target_path,status,remote_accepted
       FROM pending_production_lot_photos_v1
      WHERE tenant_id = ? AND production_id = ?
        AND status IN ('staged','pending')
      ORDER BY queued_at,id`,
    [tenantId, productionId],
  );
  const rows = (result.values ?? []) as Array<{
    id: string;
    target_path: string;
    status: 'staged' | 'pending';
    remote_accepted: number;
  }>;
  if (rows.length === 0) return;
  const now = new Date().toISOString();
  if (lifecycle.pendingScopeUncertain) return;
  if (
    lifecycle.hasPendingCompound
    && (!lifecycle.pendingCommandId || !lifecycle.pendingEvidence)
  ) return;
  if (
    !lifecycle.hasPendingCompound
    && lifecycle.masterExists
    && !lifecycle.masterEvidence
  ) return;

  const evidence = lifecycle.hasPendingCompound
    ? lifecycle.pendingEvidence
    : lifecycle.masterEvidence;
  const tasks = rows.flatMap((row) => {
    if (!evidence) {
      // Una riga pending esiste solo dopo il commit del compound: se sia il
      // comando sia il master sono spariti, il rollback è definitivo.
      return row.status === 'pending' && Number(row.remote_accepted) !== 1
        ? [{
            statement: `DELETE FROM pending_production_lot_photos_v1
              WHERE id = ? AND status = 'pending'`,
            values: [row.id],
          }]
        : [];
    }
    if (!evidence.photoPaths.has(row.target_path)) {
      return [{
        statement: `DELETE FROM pending_production_lot_photos_v1
          WHERE id = ? AND status IN ('staged','pending')`,
        values: [row.id],
      }];
    }
    if (lifecycle.hasPendingCompound) {
      return [{
        statement: `UPDATE pending_production_lot_photos_v1
          SET status = 'pending', command_id = ?, remote_accepted = 0,
              next_attempt_at = ?, updated_at = ?
          WHERE id = ? AND status IN ('staged','pending')`,
        values: [lifecycle.pendingCommandId, now, now, row.id],
      }];
    }
    return [{
      statement: `UPDATE pending_production_lot_photos_v1
        SET status = 'pending', remote_accepted = 1,
            next_attempt_at = CASE
              WHEN remote_accepted = 0 THEN ? ELSE next_attempt_at END,
            updated_at = ?
        WHERE id = ? AND status IN ('staged','pending')`,
      values: [now, now, row.id],
    }];
  });
  if (tasks.length > 0) await db.executeTransaction(tasks, true);
}

/** Sblocca soltanto le foto referenziate dal compound produzione durevole. */
export async function commitStagedProductionLotPhotos(
  productionId: string,
): Promise<void> {
  assertUuid(productionId, 'Produzione');
  const db = await openLocalDb(null);
  const captures = await db.query(
    `SELECT DISTINCT capture_id FROM pending_production_lot_photos_v1
      WHERE production_id = ? AND status = 'staged'`,
    [productionId],
  );
  const tenants = await db.query(
    `SELECT DISTINCT tenant_id FROM pending_production_lot_photos_v1
      WHERE production_id = ? AND status = 'staged'`,
    [productionId],
  );
  for (const value of tenants.values ?? []) {
    const tenantId = String((value as { tenant_id?: unknown }).tenant_id ?? '');
    if (!UUID_RE.test(tenantId)) continue;
    await reconcileProductionLifecycle(db, tenantId, productionId);
  }
  await flushLocalDb();
  for (const value of captures.values ?? []) {
    clearCaptureLease(String((value as { capture_id?: unknown }).capture_id ?? ''));
  }
}

async function reconcileCommittedRows(
  db: Awaited<ReturnType<typeof openLocalDb>>,
  tenantId: string,
): Promise<void> {
  const staged = await db.query(
    `SELECT DISTINCT production_id
       FROM pending_production_lot_photos_v1
      WHERE tenant_id = ? AND status IN ('staged','pending')`,
    [tenantId],
  );
  for (const value of staged.values ?? []) {
    const productionId = String((value as { production_id?: unknown }).production_id ?? '');
    if (!UUID_RE.test(productionId)) continue;
    await reconcileProductionLifecycle(db, tenantId, productionId);
  }
}

async function gcOrphanedStagedRows(
  db: Awaited<ReturnType<typeof openLocalDb>>,
  tenantId: string,
  now: Date,
): Promise<void> {
  const cutoff = new Date(now.getTime() - ORPHANED_STAGE_TTL_MS).toISOString();
  const result = await db.query(
    `SELECT DISTINCT production_id,capture_id
       FROM pending_production_lot_photos_v1
      WHERE tenant_id = ? AND status = 'staged' AND queued_at <= ?`,
    [tenantId, cutoff],
  );
  const lifecycles = new Map<string, ProductionLifecycle>();
  for (const value of result.values ?? []) {
    const row = value as { production_id?: unknown; capture_id?: unknown };
    const productionId = String(row.production_id ?? '');
    const captureId = String(row.capture_id ?? '');
    if (!UUID_RE.test(productionId) || !UUID_RE.test(captureId)) continue;
    if (hasCaptureLease(captureId)) continue;
    let lifecycle = lifecycles.get(productionId);
    if (!lifecycle) {
      lifecycle = await inspectProductionLifecycle(db, tenantId, productionId);
      lifecycles.set(productionId, lifecycle);
    }
    if (
      lifecycle.masterExists
      || lifecycle.hasPendingCompound
      || lifecycle.pendingScopeUncertain
    ) continue;
    await db.run(
      `DELETE FROM pending_production_lot_photos_v1
        WHERE tenant_id = ? AND production_id = ? AND capture_id = ?
          AND status = 'staged' AND queued_at <= ?`,
      [tenantId, productionId, captureId, cutoff],
    );
  }
}

function isAlreadyUploaded(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const candidate = error as { statusCode?: string | number; message?: string };
  return String(candidate.statusCode ?? '') === '409'
    || /already exists|duplicate/i.test(candidate.message ?? '');
}

let drainQueue: Promise<unknown> = Promise.resolve();

function serializeDrain<Result>(work: () => Promise<Result>): Promise<Result> {
  const running = drainQueue.then(work, work);
  drainQueue = running.then(
    () => undefined,
    () => undefined,
  );
  return running;
}

function nextRetryAt(attempts: number, now: Date): string {
  const exponent = Math.max(0, Math.min(10, Math.floor(attempts)));
  const delay = Math.min(RETRY_MAX_MS, RETRY_BASE_MS * (2 ** exponent));
  return new Date(now.getTime() + delay).toISOString();
}

async function applyCompoundFinalizations(
  db: Awaited<ReturnType<typeof openLocalDb>>,
  tenantId: string,
  now: string,
): Promise<void> {
  await db.run(
    `DELETE FROM pending_production_lot_photos_v1
      WHERE tenant_id = ? AND command_id IN (
        SELECT command_id FROM stock_command_finalizations
         WHERE outcome = 'permanently_rejected'
      )`,
    [tenantId],
  );
  await db.run(
    `UPDATE pending_production_lot_photos_v1
        SET remote_accepted = 1, next_attempt_at = ?, updated_at = ?
      WHERE tenant_id = ? AND status = 'pending' AND remote_accepted = 0
        AND command_id IN (
          SELECT command_id FROM stock_command_finalizations
           WHERE outcome = 'acknowledged'
        )
        AND command_id NOT IN (
          SELECT command_id FROM pending_stock_commands
        )`,
    [now, now, tenantId],
  );
}

/**
 * Carica un piccolo batch. Un retry dopo crash è idempotente: se l'oggetto
 * esiste già nello stesso path, verifichiamo che sia leggibile e chiudiamo la
 * riga locale senza usare upsert (la prova remota non viene mai sovrascritta).
 */
export async function drainProductionLotPhotoOutbox(
  tenantId: string,
  limit = 3,
): Promise<{ uploaded: number; failed: number }> {
  assertUuid(tenantId, 'Tenant');
  // La sessione multi-scatto conserva la banda e il thread grafico per la
  // fotocamera. Gli upload già in coda riprendono automaticamente al giro
  // successivo, senza perdere o alterare alcuna evidenza locale.
  if (operatorExternalActivityActive()) {
    return { uploaded: 0, failed: 0 };
  }
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return { uploaded: 0, failed: 0 };
  }
  return serializeDrain(async () => {
    const db = await openLocalDb(null);
    const drainStartedAt = new Date();
    await db.run(
      `UPDATE pending_production_lot_photos_v1
          SET status = 'pending', updated_at = ?
        WHERE tenant_id = ? AND status = 'uploading'`,
      [drainStartedAt.toISOString(), tenantId],
    );
    await applyCompoundFinalizations(db, tenantId, drainStartedAt.toISOString());
    await reconcileCommittedRows(db, tenantId);
    await gcOrphanedStagedRows(db, tenantId, drainStartedAt);
    const readyAt = new Date();
    const batchLimit = Number.isInteger(limit) && limit > 0
      ? Math.min(limit, 20)
      : 3;
    const scanLimit = Math.min(80, Math.max(batchLimit + 3, batchLimit * 4));
    const result = await db.query(
      `SELECT id,production_id,target_path,mime,base64,sha256,
              size_bytes,attempts
         FROM pending_production_lot_photos_v1
        WHERE tenant_id = ? AND status = 'pending' AND remote_accepted = 1
          AND review_required = 0 AND next_attempt_at <= ?
        ORDER BY next_attempt_at,attempts,queued_at,id
        LIMIT ?`,
      [tenantId, readyAt.toISOString(), scanLimit],
    );
    const rows = (result.values ?? []) as PendingPhotoRow[];
    let uploaded = 0;
    let failed = 0;
    for (const row of rows) {
      if (uploaded >= batchLimit) break;
      await db.run(
        `UPDATE pending_production_lot_photos_v1
            SET status = 'uploading', updated_at = ?
          WHERE id = ? AND status = 'pending' AND remote_accepted = 1
            AND review_required = 0 AND next_attempt_at <= ?`,
        [readyAt.toISOString(), row.id, readyAt.toISOString()],
      );
      try {
        const [, businessId = ''] = row.target_path.split('/');
        if (!isProductionEvidencePath(
          row.target_path,
          tenantId,
          businessId,
          row.production_id,
        )) {
          throw new PhotoReviewRequiredError('production_lot_photo_path_invalid');
        }
        const normalizedMime = row.mime.toLowerCase();
        if (!/^image\/(?:jpeg|jpg|png|webp)$/.test(normalizedMime)) {
          throw new PhotoReviewRequiredError('production_lot_photo_mime_invalid');
        }
        const suffix = row.target_path.split('.').pop()?.toLowerCase();
        const mimeMatchesSuffix = normalizedMime === 'image/png'
          ? suffix === 'png'
          : normalizedMime === 'image/webp'
            ? suffix === 'webp'
            : suffix === 'jpg' || suffix === 'jpeg';
        if (!mimeMatchesSuffix) {
          throw new PhotoReviewRequiredError('production_lot_photo_mime_path_mismatch');
        }
        const bytes = bytesFromBase64(row.base64);
        if (bytes.byteLength !== Number(row.size_bytes)) {
          throw new PhotoReviewRequiredError('production_lot_photo_size_invalid');
        }
        const localDigest = await sha256Hex(bytes);
        if (row.sha256 != null && !/^[a-f0-9]{64}$/i.test(row.sha256)) {
          throw new PhotoReviewRequiredError('production_lot_photo_digest_invalid');
        }
        if (row.sha256 != null && row.sha256.toLowerCase() !== localDigest) {
          throw new PhotoReviewRequiredError('production_lot_photo_digest_mismatch');
        }
        const expectedDigest = row.sha256?.toLowerCase() ?? localDigest;
        if (row.sha256 == null) {
          await db.run(
            `UPDATE pending_production_lot_photos_v1 SET sha256 = ?, updated_at = ?
              WHERE id = ? AND status = 'uploading'`,
            [expectedDigest, readyAt.toISOString(), row.id],
          );
        }
        const storage = getSupabase().storage.from(BUCKET);
        const { error } = await storage.upload(row.target_path, bytes, {
          contentType: row.mime,
          upsert: false,
        });
        if (error && !isAlreadyUploaded(error)) throw error;
        if (error) {
          const check = await storage.download(row.target_path);
          if (check.error || !check.data) {
            throw new PhotoReviewRequiredError(
              'production_lot_photo_remote_collision_unverifiable',
            );
          }
          let remoteDigest: string;
          try {
            remoteDigest = await sha256Hex(
              new Uint8Array(await check.data.arrayBuffer()),
            );
          } catch {
            throw new PhotoReviewRequiredError(
              'production_lot_photo_remote_collision_unverifiable',
            );
          }
          if (remoteDigest !== expectedDigest) {
            throw new PhotoReviewRequiredError(
              'production_lot_photo_remote_collision_digest_mismatch',
            );
          }
        }
        await db.run(
          'DELETE FROM pending_production_lot_photos_v1 WHERE id = ?',
          [row.id],
        );
        uploaded += 1;
      } catch (error) {
        const failedAt = new Date();
        if (error instanceof PhotoReviewRequiredError) {
          await db.run(
            `UPDATE pending_production_lot_photos_v1
                SET status = 'pending', review_required = 1,
                    attempts = attempts + 1, last_error = ?, updated_at = ?
              WHERE id = ?`,
            [boundedError(error), failedAt.toISOString(), row.id],
          );
          failed += 1;
          continue;
        }
        const retryAt = nextRetryAt(Number(row.attempts), failedAt);
        await db.run(
          `UPDATE pending_production_lot_photos_v1
              SET status = 'pending', attempts = attempts + 1,
                  last_error = ?, next_attempt_at = ?, updated_at = ?
            WHERE id = ?`,
          [boundedError(error), retryAt, failedAt.toISOString(), row.id],
        );
        failed += 1;
      }
    }
    await flushLocalDb();
    return { uploaded, failed };
  });
}
